import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  deleteManualFile,
  deleteRISReport,
  registerFileReference,
} from "@/features/imagings/services/medicalReportsService";

/**
 * Hook para gestionar las acciones de eliminación e informes y archivos
 */
export function useMedicalReportsActions() {
  const queryClient = useQueryClient();

  /**
   * Mutación para registrar un archivo manual (referencia)
   */
  const uploadReportMutation = useMutation({
    mutationFn: async (data: {
      file: File;
      id_study: string;
      filename: string;
      responsible: string;
      date_report: string;
      description: string;
    }) => {
      return registerFileReference(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medicalReports"] });
      queryClient.invalidateQueries({ queryKey: ["imagings"] });
      queryClient.invalidateQueries({ queryKey: ["imaging-query"] });
      toast.success("Archivo registrado correctamente");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al registrar el archivo");
    },
  });

  /**
   * Mutación para eliminar un archivo manual
   */
  const deleteFileMutation = useMutation({
    mutationFn: async ({ idFile, idStudy }: { idFile: string; idStudy: string }) => {
      return deleteManualFile(idFile, idStudy);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medicalReports"] });
      queryClient.invalidateQueries({ queryKey: ["imagings"] });
      queryClient.invalidateQueries({ queryKey: ["imaging-query"] });
      toast.success("Archivo eliminado correctamente");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al eliminar el archivo");
    },
  });

  /**
   * Mutación para limpiar un informe RIS
   */
  const deleteRISMutation = useMutation({
    mutationFn: async (reportId: string) => {
      return deleteRISReport(reportId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medicalReports"] });
      queryClient.invalidateQueries({ queryKey: ["imagings"] });
      queryClient.invalidateQueries({ queryKey: ["imaging-query"] });
      toast.success("Informe limpiado y PDF eliminado");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al limpiar el informe");
    },
  });

  return {
    uploadReport: uploadReportMutation,
    deleteFile: deleteFileMutation,
    deleteRIS: deleteRISMutation,
    isUploading: uploadReportMutation.isPending,
    isDeletingFile: deleteFileMutation.isPending,
    isDeletingRIS: deleteRISMutation.isPending,
  };
}
